import { Head, Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ReportRef {
  id: number
}

interface SourceEntry {
  id: number
  entry_text: string | null
  start_time: string | null
  user_name: string | null
}

interface Props {
  case_record: CaseRecord
  report: ReportRef
  source_entry: SourceEntry
  revision: { entry_text: string | null }
  errors?: Record<string, string[]>
}

export default function Revise({
  case_record,
  report,
  source_entry,
  revision,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: `Report #${report.id}`,
      href: `/cases/${case_record.id}/reports/${report.id}`,
    },
    { title: "Add Revision", href: "#" },
  ]

  const { data, setData, post, processing, transform } = useForm({
    entry_text: revision.entry_text ?? "",
  })

  transform((data) => ({ case_report_entry: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(
      `/cases/${case_record.id}/reports/${report.id}/entries/${source_entry.id}/revise`,
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Revision - ${case_record.case_number}`} />

      <div className="mx-auto max-w-2xl p-6">
        <h1 className="mb-4 text-2xl font-semibold">Add Revision</h1>

        <div className="border-hpi-light-gray mb-6 rounded border bg-gray-50 p-4">
          <label className="text-hpi-med-gray mb-1 block text-sm font-medium">
            Saved Entry
          </label>
          <div className="text-sm whitespace-pre-wrap">
            {source_entry.entry_text}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="entry_text"
              className="text-hpi-dark-gray mb-1 block text-sm font-medium"
            >
              Make changes to the entry below
            </label>
            <textarea
              id="entry_text"
              value={data.entry_text}
              onChange={(e) => setData("entry_text", e.target.value)}
              className="border-hpi-light-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue w-full rounded border px-3 py-2 focus:ring-1 focus:outline-none"
              rows={10}
              required
            />
            {errors?.entry_text && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.entry_text[0]}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Add Revision
            </button>
            <Link
              href={`/cases/${case_record.id}/reports/${report.id}`}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
